import React from "react";
import { Account, TransactionCategory, TransactionCategoryDetail, TransactionDetailValue, TransactionCustomFieldValues } from "../../../../types";
import { formatRate } from "../../../../utils/transactionUtils";

type TransactionType = "expense" | "income" | "transfer";
type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: TransactionDetailValue;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface UseMobileFormHandlersProps {
  transactionType: TransactionType;
  focusedField: FocusedField;
  setFocusedField: (field: FocusedField) => void;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  accounts: Account[];
  defaultCurrency: string;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  setRateManuallyEdited: (edited: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (edited: boolean) => void;
  hookHandleCategorySelect: (categoryName: string) => void;
  selectedCategoryDetails: TransactionCategoryDetail[];
  newlyAddedDetailOptions: Record<string, string[]>;
  setNewlyAddedDetailOptions: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  addOptionDialogValue: string;
  setAddOptionDialogValue: (value: string) => void;
  addOptionDialogDetailName: string;
  setAddOptionDialogDetailName: (name: string) => void;
  addOptionDialogError: string;
  setAddOptionDialogError: (error: string) => void;
  addOptionDialogOpen: boolean;
  setAddOptionDialogOpen: (open: boolean) => void;
}

/**
 * Hook for managing form handlers (account, currency, category, file, etc.)
 */
export const useMobileFormHandlers = (props: UseMobileFormHandlersProps) => {
  const {
    transactionType,
    focusedField,
    setFocusedField,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    accounts,
    defaultCurrency,
    calculateTransferRate,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    hookHandleCategorySelect,
    selectedCategoryDetails,
    newlyAddedDetailOptions,
    setNewlyAddedDetailOptions,
    addOptionDialogValue,
    setAddOptionDialogValue,
    addOptionDialogDetailName,
    setAddOptionDialogDetailName,
    addOptionDialogError,
    setAddOptionDialogError,
    addOptionDialogOpen,
    setAddOptionDialogOpen,
  } = props;

  // Handle account selection
  const handleAccountSelect = (accountId: string) => {
    const account = accounts.find(acc => acc.id === accountId);
    if (account) {
      if (transactionType === "transfer") {
        if (focusedField === "fromAccount") {
          setTransferFormData({
            ...transferFormData,
            fromAccountId: accountId,
            toAccountId: accountId === transferFormData.toAccountId ? "" : transferFormData.toAccountId,
          });
          // Auto-advance to toAccount if not selected
          if (!transferFormData.toAccountId || transferFormData.toAccountId === accountId) {
            setFocusedField("toAccount");
          } else if (!transferFormData.date || transferFormData.date.trim() === "") {
            // toAccount is already selected, go to date if not filled
            setFocusedField("date");
          } else {
            setFocusedField(null);
          }
        } else if (focusedField === "toAccount") {
          setTransferFormData({
            ...transferFormData,
            toAccountId: accountId,
          });
          // Auto-advance to date if not filled
          if (!transferFormData.date || transferFormData.date.trim() === "") {
            setFocusedField("date");
          } else {
            setFocusedField(null);
          }
        } else if (focusedField === "feeAccount") {
          setTransferFormData({
            ...transferFormData,
            feeAccountId: accountId,
          });
          // Close panel after selecting fee account
          setFocusedField(null);
        }
      } else {
        // Don't change currency when account is selected - keep the selected currency (default currency)
        setFormData({
          ...formData,
          accountId: accountId,
          accountGroup: account.group,
          // Keep current currency (don't change to account currency)
        });
        
        // Auto-advance to next required field
        // Priority: category -> date (if not filled)
        if (!formData.category) {
          // Category is not selected, go to category
          setFocusedField("category");
        } else if (!formData.date || formData.date.trim() === "") {
          // Category is selected but date is not, go to date
          setFocusedField("date");
        } else {
          // All required fields are filled, close the panel
          setFocusedField(null);
        }
      }
    }
  };

  // Handle currency selection
  const handleCurrencySelect = (currencyCode: string) => {
    if (!currencyCode) {
      return;
    }

    if (transactionType === "transfer") {
      // For transfers, currency is determined by accounts
      setFocusedField(null);
      return;
    }

    const account = accounts.find(acc => acc.id === formData.accountId);
    const accountCurrency = account?.currency || defaultCurrency;

    let rate = "";
    if (currencyCode !== accountCurrency) {
      rate = formatRate(calculateTransferRate(currencyCode, accountCurrency));
    }

    // Field stores account-to-default rate (not expense-to-default rate)
    // This matches desktop behavior and is what useMobileTransactionSubmission expects
    let defaultCurrencyRate = "";
    if (accountCurrency !== defaultCurrency) {
      defaultCurrencyRate = formatRate(calculateTransferRate(accountCurrency, defaultCurrency));
    }

    setFormData({
      ...formData,
      currency: currencyCode,
      rate: rate,
      defaultCurrencyRate: defaultCurrencyRate,
    });
    // Reset flags - rates are from settings
    setRateManuallyEdited(false);
    setDefaultCurrencyRateManuallyEdited(false);
    setFocusedField(null);
  };

  // Use hookHandleCategorySelect from the hook, but wrap it to auto-advance to next field
  const handleCategorySelect = (categoryName: string) => {
    // Handle the category selection first
    hookHandleCategorySelect(categoryName);
    
    // Auto-advance to next required field after category is selected
    // Priority: date (if not filled)
    setTimeout(() => {
      if (transactionType !== "transfer") {
        if (!formData.date || formData.date.trim() === "") {
          // Date is not filled, go to date field
          setFocusedField("date");
        } else {
          // All required fields are filled, close the panel
          setFocusedField(null);
        }
      } else {
        // For transfers, just close after category selection
        setFocusedField(null);
      }
    }, 100);
  };

  // Check for duplicate option (live detection)
  const checkDuplicateOption = (value: string, detailName: string): string => {
    if (!value.trim()) {
      return "";
    }

    const trimmedValue = value.trim();
    const detail = selectedCategoryDetails.find(d => d.name === detailName);
    
    // Check if option already exists in original listItems
    if (detail && detail.listItems && detail.listItems.includes(trimmedValue)) {
      return "This option already exists in the list.";
    }
    
    // Check if option already exists in newly added options
    const existingNewOptions = newlyAddedDetailOptions[detailName] || [];
    if (existingNewOptions.includes(trimmedValue)) {
      return "This option has already been added.";
    }

    return "";
  };

  // Handle adding new detail option
  const handleAddDetailOption = () => {
    if (!addOptionDialogValue.trim()) {
      setAddOptionDialogError("Option name cannot be empty.");
      return;
    }

    const detailName = addOptionDialogDetailName;
    const newOption = addOptionDialogValue.trim();
    
    // Check for duplicates
    const error = checkDuplicateOption(newOption, detailName);
    if (error) {
      setAddOptionDialogError(error);
      return;
    }

    // Clear error if validation passes
    setAddOptionDialogError("");

    // Add the new option
    const existingNewOptions = newlyAddedDetailOptions[detailName] || [];
    const updatedOptions = [...existingNewOptions, newOption];
    setNewlyAddedDetailOptions({
      ...newlyAddedDetailOptions,
      [detailName]: updatedOptions,
    });

    // Auto-select the newly added option
    setFormData({
      ...formData,
      details: {
        ...(formData.details || {}),
        [detailName]: newOption,
      },
    });

    // Close dialog
    setAddOptionDialogOpen(false);
    setAddOptionDialogValue("");
    setAddOptionDialogDetailName("");
    setAddOptionDialogError("");
  };

  // Handle closing dialog without adding
  const handleCloseAddOptionDialog = () => {
    setAddOptionDialogOpen(false);
    setAddOptionDialogValue("");
    setAddOptionDialogDetailName("");
    setAddOptionDialogError("");
  };

  return {
    handleAccountSelect,
    handleCurrencySelect,
    handleCategorySelect,
    checkDuplicateOption,
    handleAddDetailOption,
    handleCloseAddOptionDialog,
  };
};
